import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, Injectable, Injector, NgZone } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthService } from '../auth/auth.service';
import { ErrorService } from './services/error.service';
import { LoggingService } from './services/logging.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {

  testEnvironments: string[] = [
    'localhost',
    'dev.anessa.com',
    'qa.anessa.com',
  ];

  constructor(
    private injector: Injector,
    private ngZone: NgZone,
    private snackBar: MatSnackBar,
  ) { }

  handleError(error: Error | HttpErrorResponse): void {
    const authService = this.injector.get(AuthService);
    const errorService = this.injector.get(ErrorService);
    const logger = this.injector.get(LoggingService);

    let message: string;
    let title: string;
    let stackTrace: string;

    // Enable original error dump on localhost and qa
    const hostname = window.location.host;
    const isDev = this.testEnvironments.some(host => hostname.includes(host));
    if (isDev) {
      console.log('Raw Error');
      console.log('----------------------------------------------------------');
      console.error(error);
      console.log('----------------------------------------------------------');
    }

    if (error instanceof HttpErrorResponse) {
      // handle server error
      message = errorService.getServerErrorMessage(error);
      title = errorService.getServerErrorTitle(error);
      stackTrace = errorService.getServerStack(error);

      // Handle Http errors (error.status === 403, 404, custom...)
      if (error.status === 401) {
        this.ngZone.run(() => {
          // Ensure the user is logged out before redirect to handle expired auth token
          authService.logout();
        });
        if (!message.includes('invalid authentication token')) {
          errorService.showError(message);
        }
      } else if (error.status === 502) {
        // Hide 502 errors unless on dev environments
        if (isDev) {
          console.log(error.url);
          errorService.showError(error.message, error.statusText);
        } else {
          console.log('ignoring encountered 502 error');
        }
      } else if (error.status === 0) { // supress request has been aborted errors (i.e during sso redirection)
        if (isDev) {
          console.log(error)
        } else {
          console.log("ignoring encountered 0 error")
        }
      } else {
        // Show HTTP error message details if not server error message
        if (isDev) {
          if (title == null) {
            title = error.statusText;
          }
          if (message == null) {
            message = error.message;
          }
        }
        errorService.showError(message, title);
      }


    } else {
      // handle client Error
      message = errorService.getClientErrorMessage(error);
      stackTrace = errorService.getClientStack(error);

      /**
       * these errors are basically warnings that a variable has changed
       * before the view has been updated. They do not get thrown when building
       * with prod mode, and they are still getting thrown on local. They should
       * be corrected and prevented but they do not affect the user or have
       * a major impact of functionality
       */
      if (message.includes('ExpressionChangedAfterItHasBeenCheckedError')) {
        return;
      }

      errorService.showError(message);
    }

    this.snackBar.dismiss();

    // Always log errors
    logger.logError(message, stackTrace);
  }
}
